import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

// Phase 2 i18n — translator.trans() 返回 vnode array，flatten 为 string。
// WHY: see memory/flarum-1.8-pitfalls.md 坑 B
function t(key) {
  const result = app.translator.trans(key);
  return Array.isArray(result) ? result.join('') : result;
}
function tRich(key, params) {
  return app.translator.trans(key, params || {});
}

// 装备类型定义（label 改 function lazy 调用）
const EQUIPMENT_TYPE_MAP = {
  clothing: {
    label: () => t('ziven-dress-up.forum.common.equipment_clothing'),
    icon: 'fa-tshirt'
  },
  pants: {
    label: () => t('ziven-dress-up.forum.common.equipment_pants'),
    icon: 'fa-socks'
  },
  helmet: {
    label: () => t('ziven-dress-up.forum.common.equipment_helmet'),
    icon: 'fa-hard-hat'
  },
  boots: {
    label: () => t('ziven-dress-up.forum.common.equipment_boots'),
    icon: 'fa-skiing'
  },
  gloves: {
    label: () => t('ziven-dress-up.forum.common.equipment_gloves'),
    icon: 'fa-mitten'
  },
  goggles: {
    label: () => t('ziven-dress-up.forum.common.equipment_goggles'),
    icon: 'fa-glasses'
  },
  snowboard: {
    label: () => t('ziven-dress-up.forum.common.equipment_snowboard'),
    icon: 'fa-skate'
  },
  backpack: {
    label: () => t('ziven-dress-up.forum.common.equipment_backpack'),
    icon: 'fa-briefcase'
  },
  background: {
    label: () => t('ziven-dress-up.forum.common.equipment_background'),
    icon: 'fa-image'
  }
};
export default class SeedreamDetailModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.record = this.attrs.record;
    this.fullRecord = null; // 完整数据
    this.loading = true; // 加载状态
    this.fetchError = null; // 加载错误/超时提示
    this.deleting = false;
    this.regenerating = false;
    this.onDeleted = this.attrs.onDeleted || (() => {});
    this.onRegenerated = this.attrs.onRegenerated || (() => {});
    // 在弹窗打开时获取完整数据
    this.fetchFullRecord();
  }
  async fetchFullRecord() {
    try {
      const response = await app.request({
        method: 'GET',
        timeout: 30000,
        url: '/api/ziven/seedream/history/' + this.record.id
      });
      if (response.success) {
        this.fullRecord = response.data;
      } else {
        // API 获取失败时使用原有数据
        this.fullRecord = this.record;
        this.fetchError = response.error || t('ziven-dress-up.forum.detail_modal.load_error');
      }
    } catch (error) {
      console.error('Failed to load detail:', error);
      this.fullRecord = this.record;
      this.fetchError = error.message || t('ziven-dress-up.forum.detail_modal.load_timeout');
    }
    this.loading = false;
    m.redraw();
  }
  className() {
    return 'Modal--large SeedreamDetailModal SeedreamHistoryPage';
  }
  isStuck(record) {
    if (record.status !== 0) return false;
    if (!record.created_at) return false;
    const created = new Date(record.created_at);
    const now = new Date();
    const minutesOld = (now - created) / 1000 / 60;
    return minutesOld > 10;
  }
  title() {
    return t('ziven-dress-up.forum.detail_modal.title');
  }
  async deleteRecord() {
    if (this.deleting) return;
    if (!confirm(t('ziven-dress-up.forum.detail_modal.delete_confirm'))) {
      return;
    }
    this.deleting = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'DELETE',
        url: '/api/ziven/seedream/history/' + this.record.id
      });
      if (response.success) {
        this.onDeleted(this.record.id);
        this.hide();
      } else {
        alert(response.error || t('ziven-dress-up.forum.detail_modal.delete_failed'));
      }
    } catch (error) {
      console.error('Failed to delete record:', error);
      alert(t('ziven-dress-up.forum.detail_modal.delete_failed_retry'));
    }
    this.deleting = false;
    m.redraw();
  }
  async regenerateRecord() {
    if (this.regenerating) return;
    if (!confirm(t('ziven-dress-up.forum.detail_modal.regenerate_confirm'))) {
      return;
    }
    this.regenerating = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'POST',
        url: '/api/ziven/seedream/regenerate/' + this.record.id
      });
      if (response.success) {
        if (response.data.task_id) {
          alert(t('ziven-dress-up.forum.detail_modal.regenerate_task_created'));
        } else if (response.data.image_url) {
          alert(t('ziven-dress-up.forum.detail_modal.regenerate_success'));
        }
        this.onRegenerated(this.record.id);
        this.hide();
      } else {
        alert(response.error || t('ziven-dress-up.forum.detail_modal.regenerate_failed'));
      }
    } catch (error) {
      console.error('Failed to regenerate record:', error);
      alert(t('ziven-dress-up.forum.detail_modal.regenerate_failed_retry'));
    }
    this.regenerating = false;
    m.redraw();
  }
  content() {
    // 如果正在加载，显示加载状态
    if (this.loading) {
      return m("div", {
        class: "Modal-body"
      }, m("div", {
        class: "loading-area"
      }, m(LoadingIndicator, {
        size: "large"
      }), m("span", null, t('ziven-dress-up.forum.common.loading'))));
    }

    // 使用完整数据，如果没有则使用原数据
    const record = this.fullRecord || this.record;

    // 解析数据格式（equipment_urls + equipment_type_mapping）
    // equipment_urls: { type: url } 格式，如 {clothing: "url1", pants: "url2"}
    // equipment_type_mapping: [[type1, type2], [type3]] 格式，每项代表一张图片的类型数组
    let equipmentPhotos = [];
    if (record.equipment_urls && record.equipment_type_mapping) {
      try {
        const urls = typeof record.equipment_urls === 'string' ? JSON.parse(record.equipment_urls) : record.equipment_urls;
        const mapping = typeof record.equipment_type_mapping === 'string' ? JSON.parse(record.equipment_type_mapping) : record.equipment_type_mapping;
        if (Array.isArray(mapping)) {
          // 遍历每张图片的类型数组（每个元素是一个类型数组，代表同一张图片的多个类型）
          mapping.forEach((types, index) => {
            if (Array.isArray(types) && types.length > 0) {
              // 获取该图片关联的第一个类型的URL
              const firstType = types[0];
              const url = urls[firstType];
              if (url) {
                equipmentPhotos.push({
                  dataUrl: url,
                  types: types
                });
              }
            }
          });
        }
      } catch (e) {
        console.error('Failed to parse equipment data:', e);
      }
    }
    return m("div", {
      class: "Modal-body"
    }, m("div", {
      class: "detail-section"
    }, m("h4", null, m("i", {
      class: "fas fa-image"
    }), t('ziven-dress-up.forum.detail_modal.fusion_result')), m("div", {
      class: "detail-image-container".concat(record.status === 2 && !record.result_image_url ? ' detail-image-container-failed' : '')
    }, record.result_image_url ? m("img", {
      src: record.result_image_url,
      alt: "Result",
      onclick: () => app.fancyboxOpen(record.result_image_url),
      style: {
        cursor: 'pointer'
      }
    }) : record.status === 0 && this.isStuck(record) ? m("div", {
      class: "no-image stuck"
    }, m("i", {
      class: "fas fa-exclamation-triangle"
    }), m("span", null, t('ziven-dress-up.forum.detail_modal.generation_timeout'))) : record.status === 2 ?
    // v0.4.15: 失败卡 (status=2) 走"失败"占位, 跟状态行"融合失败"对齐, 不再误显示"加载中..."
    m("div", {
      class: "no-image failed"
    }, m("i", {
      class: "fas fa-times-circle"
    }), m("span", null, record.error_message || t('ziven-dress-up.forum.detail_modal.generation_failed'))) : record.status === 0 ? m("div", {
      class: "no-image generating"
    }, m(LoadingIndicator, {
      size: "small"
    }), m("span", null, t('ziven-dress-up.forum.detail_modal.generating'))) : m("div", {
      class: "no-image"
    }, t('ziven-dress-up.forum.common.loading')))), m("div", {
      class: "detail-section"
    }, m("h4", null, m("i", {
      class: "fas fa-images"
    }), t('ziven-dress-up.forum.detail_modal.source_image')), m("div", {
      class: "source-images"
    }, record.person_avatar_url ? m("div", {
      class: "source-item"
    }, m("div", {
      class: "source-thumb"
    }, m("img", {
      src: record.person_avatar_url,
      alt: "Person",
      onclick: () => app.fancyboxOpen(record.person_avatar_url),
      style: {
        cursor: 'pointer'
      }
    })), m("div", {
      class: "source-type-tags"
    }, m("span", {
      class: "type-tag type-tag-person"
    }, m("i", {
      class: "fas fa-user"
    }), t('ziven-dress-up.forum.detail_modal.person_image')))) : null, equipmentPhotos.map((photo, index) => m("div", {
      class: "source-item",
      key: "eq-".concat(index)
    }, m("div", {
      class: "source-thumb"
    }, m("img", {
      src: photo.dataUrl,
      alt: "Equipment",
      onclick: () => app.fancyboxOpen(photo.dataUrl),
      style: {
        cursor: 'pointer'
      }
    })), m("div", {
      class: "source-type-tags"
    }, photo.types.map(typeKey => {
      const typeInfo = EQUIPMENT_TYPE_MAP[typeKey];
      if (!typeInfo) return null;
      return m("span", {
        class: "type-tag",
        key: typeKey
      }, m("i", {
        class: "fas ".concat(typeInfo.icon)
      }), typeof typeInfo.label === 'function' ? typeInfo.label() : typeInfo.label);
    })))), record.background_image ? m("div", {
      class: "source-item"
    }, m("div", {
      class: "source-thumb"
    }, m("img", {
      src: record.background_image,
      alt: "Background",
      onclick: () => app.fancyboxOpen(record.background_image),
      style: {
        cursor: 'pointer'
      }
    })), m("div", {
      class: "source-type-tags"
    }, m("span", {
      class: "type-tag type-tag-background"
    }, m("i", {
      class: "fas fa-image"
    }), t('ziven-dress-up.forum.detail_modal.background_image')))) : null)), m("div", {
      class: "detail-status-row"
    }, m("div", {
      class: "meta-item"
    }, m("i", {
      class: "fas fa-clock"
    }), m("span", null, this.formatDate(record.created_at))), record.status === 0 && !this.isStuck(record) && m("div", {
      class: "meta-item generating"
    }, m(LoadingIndicator, {
      size: "small"
    }), m("span", null, t('ziven-dress-up.forum.detail_modal.generating'))), record.status === 1 && m("div", {
      class: "meta-item success"
    }, m("i", {
      class: "fas fa-check-circle"
    }), m("span", null, t('ziven-dress-up.forum.detail_modal.fusion_complete_bang'))), record.status === 2 && m("div", {
      class: "meta-item failed"
    }, m("i", {
      class: "fas fa-times-circle"
    }), m("span", null, t('ziven-dress-up.forum.detail_modal.fusion_failed')))), m("div", {
      class: "SeedreamDetailModal-footer"
    }, m(Button, {
      class: "btn-glass",
      onclick: () => app.modal.close()
    }, m("i", {
      class: "fas fa-times"
    }), t('ziven-dress-up.forum.detail_modal.close')), m(Button, {
      class: "btn-amber",
      disabled: this.regenerating,
      onclick: () => this.regenerateRecord()
    }, this.regenerating ? m('[', null, m(LoadingIndicator, null), m("span", null, t('ziven-dress-up.forum.detail_modal.regenerating'))) : m('[', null, m("i", {
      class: "fas fa-redo"
    }), m("span", null, t('ziven-dress-up.forum.detail_modal.regenerate')))), (record.status !== 0 || this.isStuck(record)) && m(Button, {
      class: "btn-danger-soft",
      disabled: this.deleting,
      onclick: () => this.deleteRecord()
    }, this.deleting ? m('[', null, m(LoadingIndicator, null), m("span", null, t('ziven-dress-up.forum.detail_modal.deleting'))) : m('[', null, m("i", {
      class: "fas fa-trash"
    }), m("span", null, t('ziven-dress-up.forum.detail_modal.delete'))))));
  }
  formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
_defineProperty(SeedreamDetailModal, "isDismissibleViaBackdropClick", false);
_defineProperty(SeedreamDetailModal, "isDismissibleViaCloseButton", true);
flarum.reg.add('ziiven-dress-up', 'forum/components/SeedreamDetailModal', SeedreamDetailModal);